import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { sendDirectEmail } from "../../../utils/apiUtils";
import {
    OBSERVABILITY_LOG_PREFIXES,
    TEST_EMAIL_TEMPLATE_TOAST_OPTIONS,
    TOAST_MESSAGES,
} from "./constants";
import { extractBackendErrorMessage } from "./testEmailTemplateSchema";

/**
 * TanStack Query mutation hook for the Test Email Template send action.
 * On success: shows a success toast.
 * On error: emits a `console.error` (no PII — status code and backend message only) and shows
 * an error toast with the backend-supplied message or a fallback string.
 *
 * @param {{ onSuccess: () => void }} callbacks
 *   `onSuccess` is called after the toast fires so the parent can clear the `toEmail` field.
 * @returns {{
 *   mutate: (body: object) => void,
 *   isPending: boolean,
 *   isError: boolean
 * }}
 */
export function useSendDirectEmail({ onSuccess }) {
    const mutation = useMutation({
        mutationFn: sendDirectEmail,
        onSuccess: () => {
            toast.success(TOAST_MESSAGES.SUCCESS, TEST_EMAIL_TEMPLATE_TOAST_OPTIONS);
            onSuccess();
        },
        onError: (error) => {
            const statusCode = error?.cause?.status ?? "";
            const message = error?.message ?? "";
            console.error(
                OBSERVABILITY_LOG_PREFIXES.SUBMIT_FAILED,
                statusCode,
                message
            );
            toast.error(
                extractBackendErrorMessage(error),
                TEST_EMAIL_TEMPLATE_TOAST_OPTIONS
            );
        },
    });

    return {
        mutate: mutation.mutate,
        isPending: mutation.isPending,
        isError: mutation.isError,
    };
}
